// @givmo/org-console — the three calm states every panel shares.
//
// ⚠ ABSENT IS NOT AN ERROR, AND IT IS NOT A DEAD END. When the console is dark in an environment the
// panels render {@link OrgConsoleUnavailableNotice} — a considered notice, never an error surface,
// never a toast, and deliberately with NO retry: the answer will not change, and a retry control
// invites someone to keep asking a question that has already been answered. The two states are
// different facts and a reader deserves the true one — "this environment has not turned the console
// on" is not "something went wrong".
//
// This window is real and it is the length of a promotion round: the web build knob and the backend
// flag are independent, so a revealed console can meet a backend that has not been switched on yet.
// Making that window survivable is the whole job of this file.
//
// ⚠ THE OTHER TWO STATES ARE NOT INTERCHANGEABLE. {@link OrgTierUnknownNotice} means the standing
// read FAILED — recoverable, so it is the ONE state that offers a retry. {@link OrgNotPermittedNotice}
// means the read SUCCEEDED and said no — not recoverable, so it offers guidance instead. Collapsing
// them tells someone "only a Global Admin can do this" after a network error, which asserts something
// the surface never learned.

import { Button, Card, CardBody, EmptyState, Icon, testIdProp } from '@givmo/design-system';
import { Lock } from 'lucide-react';

export interface OrgNoticeProps {
  testId: string;
  title: string;
  description: string;
}

/** The console is not switched on in this environment. Calm, terminal, no retry. */
export function OrgConsoleUnavailableNotice({ testId, title, description }: OrgNoticeProps) {
  return (
    <Card>
      <CardBody className="flex flex-col gap-8">
        <h3 className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{title}</h3>
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          {...testIdProp(testId)}
        >
          {description}
        </p>
      </CardBody>
    </Card>
  );
}

export interface OrgTierUnknownProps {
  testId: string;
  onRetry: () => void;
}

/**
 * What a failed standing read says, in ONE place.
 *
 * Exported because two surfaces render this state now — a panel inside a page, and the whole-page
 * gate a direct link lands on — and the sentence is the difference between "something went wrong"
 * and "you may not be here". Two copies is how one of them drifts into asserting the second.
 */
export const ORG_TIER_UNKNOWN_LINE = 'We couldn’t check your access just now.';

/** The standing read failed. The one recoverable state, and the only one with a retry. */
export function OrgTierUnknownNotice({ testId, onRetry }: OrgTierUnknownProps) {
  return (
    <Card>
      <CardBody className="flex flex-col items-start gap-12">
        <p className="font-sans text-s14 text-text-secondary">{ORG_TIER_UNKNOWN_LINE}</p>
        <Button appearance="saas" tone="secondary" size="sm" onClick={onRetry} testId={testId}>
          Try again
        </Button>
      </CardBody>
    </Card>
  );
}

/** The standing read succeeded and said no. Guidance, not a retry. */
export function OrgNotPermittedNotice({ testId, title, description }: OrgNoticeProps) {
  return (
    <EmptyState
      testId={testId}
      icon={<Icon icon={Lock} size={20} aria-hidden />}
      title={title}
      description={description}
    />
  );
}
